import {inject, Injectable, OnInit} from '@angular/core';
import {AlertController, ToastController} from '@ionic/angular';
import {Router} from '@angular/router';
import {SplashScreen} from '@capacitor/splash-screen';
import {BehaviorSubject, Observable, retry} from 'rxjs';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {AppTutorials, IAppTutorial} from '../interfaces/IAppTutorial';

@Injectable({
    providedIn: 'root'
})
export class UtilityService implements OnInit {
    private readonly alertController: AlertController = inject(AlertController);
    public readonly toastController: ToastController = inject(ToastController);
    private readonly router: Router = inject(Router);
    private readonly http: HttpClient = inject(HttpClient);
    urlApi = environment.urlApi;
    public loading: BehaviorSubject<number> = new BehaviorSubject(0);
    public deviceInfoMessage = '';

    constructor() {
    }

    ngOnInit() {
    }


    async presentAlertErrorGeneric(errorCode: number, error: any) {
        const alert = await this.alertController.create({
            header: 'Errore ' + errorCode,
            message: 'Si è verificato un errore durante il caricamento.'
                + ' Prova a riavviare l\'app. Se il problema persiste fai uno screenshot e invialo a info@promomania.it per ricevere assistenza.',
            buttons: ['OK']
        });
        await alert.present();

        this.saveErrorLog(errorCode, error).subscribe(() => {
        });
    }

    saveErrorLog(errorCode: number, error: any): Observable<any> {
        const url = error?.url ?? 'no url';
        const errorMessage = error
            ? `${error.message} - ${url}`
            : `error è null - ${url}`;

        const errorObj = {
            errorCode,
            errorMessage,
            deviceInfo: this.deviceInfoMessage
        };

        return this.http
            .post<any>(`${this.urlApi}${environment.ERROR_LOG_CREATE}`, errorObj)
            .pipe(
                retry({count: 5, delay: 1000})
            );
    }


    valueOfLoading(n) {
        const value = this.loading.getValue() + n;
        this.loading.next(value);
    }


    async valueOfSplashScreen() {
        try {
            console.log('Splash Screen.hide');
            await SplashScreen.hide({ fadeOutDuration: 300 });
        } catch (error) {
            console.log('Errore SplashScreenHide: ', error);
        }
    }


    async showAlertPermessiNotifiche() {
        const alert = await this.alertController.create({
            header: 'Permesso negato',
            message: 'Non hai concesso i permessi di ricevere le Notifiche all\'applicazione. ' +
                'Modifica le tue impostazioni oppure contattaci per email su info@promomania.it per assistenza.',
            buttons: ['OK']
        });
        await alert.present();
    }

    async showAlertPermessiCondivisione() {
        const alert = await this.alertController.create({
            header: 'Permesso negato',
            message: 'Non hai concesso i permessi di condivisione all\'applicazione. ' +
                'Modifica le tue impostazioni oppure contattaci per email su info@promomania.it per assistenza.',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentAlertWithOk(headerMsg, msg) {
        const alert = await this.alertController.create({
            header: headerMsg,
            message: msg,
            buttons: ['OK']
        });
        await alert.present();
    }

    /*** START TOAST ***/

    async presentToastSuccess(msg: string) {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'checkmark-circle-outline',
            message: msg,
            duration: 2500,
            cssClass: 'tabs-bottom',
        });
        toast.present();
    }

    async presentToastError(msg: string) {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'close-circle-outline',
            message: msg,
            duration: 2500,
            cssClass: 'tabs-bottom',
        });
        toast.present();
    }

    async presentToastWarning(msg: string) {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'warning-outline',
            message: msg,
            duration: 2500,
            cssClass: 'tabs-bottom',
        });
        toast.present();
    }

    async presentToastInfo(msg: string) {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'information-circle-outline',
            message: msg,
            duration: 2500,
            cssClass: 'tabs-bottom',
        });
        toast.present();
    }

    async presentToastNotification() {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'notifications-circle-outline',
            message: 'É arrivata una nuova notifica!',
            duration: 3000,
            cssClass: 'tabs-bottom',
            buttons: [
                {
                    side: 'end',
                    text: 'Controlla',
                    handler: () => {
                        this.router.navigateByUrl('/home/notifications');
                    }
                }
            ]
        });
        await toast.present();
    }

    async presentToastUtenteNonLoggato() {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'warning-outline',
            message: 'Devi accedere col tuo account per effettuare quest\'azione',
            duration: 3000,
            cssClass: 'tabs-bottom',
            buttons: [
                {
                    side: 'end',
                    text: 'Accedi',
                    handler: () => {
                        this.router.navigateByUrl('/login');
                    }
                }
            ]
        });
        toast.present();
    }

    async presentToastAccountNonVerificato() {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'warning-outline',
            message: 'Devi verificare l\'email del tuo account per effetturare quest\'azione',
            duration: 2500,
            cssClass: 'tabs-bottom',
            buttons: [
                {
                    side: 'end',
                    text: 'Account',
                    handler: () => {
                        this.router.navigateByUrl('/login');
                    }
                }
            ]
        });
        toast.present();
    }

    async presentToastWarningTop(msg: string) {
        const toast = await this.toastController.create({
            color: 'medium',
            position: 'top',
            icon: 'warning-outline',
            message: msg,
            duration: 3000,
            cssClass: 'tabs-top',
        });
        toast.present();
    }

    async presentToastNotificationError() {
        const toast = await this.toastController.create({
            color: 'medium',
            icon: 'notifications-circle-outline',
            message: 'Si è verificato un errore con le notifiche, sono state ripristinate!',
            duration: 3000,
            cssClass: 'tabs-bottom',
            buttons: [
                {
                    side: 'end',
                    text: 'Controlla',
                    handler: () => {
                        this.router.navigateByUrl('/notifications-setting');
                    }
                }
            ]
        });
        await toast.present();
    }

    /*** START API ***/

    getAppTutorials(): Observable<IAppTutorial[]> {
        const url = `${environment.urlApi}${environment.APP_TUTORIAL_GET_ALL}`;
        return this.http.get<AppTutorials>(url).pipe(
            retry({count: 5, delay: 1000}),
            map(res => res.data)
        );
    }

    getAppTermsAndConditions(type: string): Observable<any> {
        const url = `${environment.urlApi}${environment.TERMS_AND_CONDITIONS_GET_LAST}`;
        return this.http.post<{ data: any }>(url, {type}).pipe(
            retry({count: 5, delay: 1000}),
            map(res => res.data)
        );
    }

}
